/**
 * Performance instrumentation for Focus Shell login flow.
 *
 * Uses the browser Performance API (mark/measure) so timings
 * appear in DevTools > Performance and can be read programmatically.
 *
 * Usage:
 *   perf.mark('auth:start')
 *   // ... work ...
 *   perf.measure('auth:license-check', 'auth:start')  // measures from mark to now
 *
 *   // Or use the span helper for automatic start/end:
 *   const end = perf.span('entitlements:fetch')
 *   await fetch(...)
 *   end()  // logs and creates measure
 *
 *   // Print summary at the end:
 *   perf.summary()
 */

const PREFIX = '[Focus Perf]'
const ENABLED = typeof window !== 'undefined'

/** Place a performance mark */
function mark(name: string) {
  if (!ENABLED) return
  performance.mark(name)
}

/** Measure from a previous mark to now. Returns duration in ms. */
function measure(label: string, fromMark: string): number {
  if (!ENABLED) return 0
  try {
    const m = performance.measure(label, fromMark)
    const ms = Math.round(m.duration)
    console.log(`${PREFIX} ${label}: ${ms}ms`)
    return ms
  } catch {
    return 0
  }
}

/** Convenience: marks start, returns a function that measures on call. */
function span(name: string): () => number {
  const startMark = `${name}:start`
  mark(startMark)
  return () => measure(name, startMark)
}

/** Print a summary table of all Focus Perf measures. */
function summary() {
  if (!ENABLED) return
  const entries = performance.getEntriesByType('measure')
    .filter(e => e.name.startsWith('focus:') || e.name.includes(':'))
    .sort((a, b) => a.startTime - b.startTime)

  if (entries.length === 0) return

  console.group(`${PREFIX} === LOGIN TIMELINE ===`)
  const t0 = entries[0]?.startTime ?? 0
  for (const e of entries) {
    const offset = Math.round(e.startTime - t0)
    const dur = Math.round(e.duration)
    const bar = '█'.repeat(Math.max(1, Math.round(dur / 100)))
    console.log(
      `+${String(offset).padStart(5)}ms  ${bar} ${dur}ms  ${e.name}`
    )
  }
  const total = entries.length > 0
    ? Math.round(entries[entries.length - 1].startTime + entries[entries.length - 1].duration - t0)
    : 0
  console.log(`\nTotal wall time: ${total}ms`)
  console.groupEnd()
}

/** Clear all marks and measures (call on logout/re-login). */
function clear() {
  if (!ENABLED) return
  performance.clearMarks()
  performance.clearMeasures()
}

export const perf = { mark, measure, span, summary, clear }
